import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Target, Eye, Heart, ShieldCheck, HardHat, Award, Users } from "lucide-react";
import SEO from "../components/SEO";
import SectionHeader from "../components/SectionHeader";
import { useSiteData, useLocaleText } from "../contexts/SiteDataContext";

const About = () => {
  const { t } = useTranslation();
  const { siteContent } = useSiteData();
  const { pick } = useLocaleText();
  const values = siteContent?.values || [];
  const stats = siteContent?.stats || [];

  return (
    <div data-testid="page-about">
      <SEO title={t("nav.about")} description={pick(siteContent?.about_body)} path="/about" />

      <section className="relative bg-[#111111] text-white">
        <div className="absolute inset-0">
          <img src="/hero-about.jpg" alt="RTC heavy equipment at work" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/95 via-[#111111]/50 to-transparent rtl:bg-gradient-to-l" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-8 md:py-12">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-[2px] bg-[#FF5A00]" />
            <span className="font-mono-rtc text-[11px] uppercase tracking-[0.3em] text-[#FF5A00]">{t("about.eyebrow")}</span>
          </div>
          <h1 className="font-display uppercase font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="about-hero-title">
            {pick(siteContent?.about_title) || t("about.title")}
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/80 leading-relaxed">{t("about.intro")}</p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32 space-y-8">
            <SectionHeader eyebrow={t("about.storyEyebrow")} title={t("about.storyTitle")} />
            {stats.length > 0 && (
              <div className="grid grid-cols-2 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
                {stats.map((s, i) => (
                  <div key={i} className="bg-white p-4 md:p-6 hover:bg-[#111111] transition-colors duration-300 group cursor-default">
                    <div className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#FF5A00]">{s.value}</div>
                    <div className="mt-1 font-mono-rtc text-[10px] uppercase tracking-[0.2em] text-[#52525B] group-hover:text-white/70 transition-colors duration-300">
                      {pick({ en: s.label_en, ar: s.label_ar })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-7 space-y-5 text-[#111111]/85 text-base md:text-lg leading-relaxed">
            {(() => {
              const body = pick(siteContent?.about_body);
              const paragraphs = body ? body.split(/\n\n+/).filter(Boolean) : [t("about.para1"), t("about.para2"), t("about.para3")];
              return paragraphs.map((para, i) => <p key={i}>{para}</p>);
            })()}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-[#FAFAFA] grid-bg">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-8">
          <div className="bg-white border-2 border-[#111111] block-shadow p-10" data-testid="mission-card">
            <Target className="text-[#FF5A00]" size={32} />
            <span className="font-mono-rtc text-[11px] uppercase tracking-[0.3em] text-[#52525B] block mt-4">{t("about.missionLabel")}</span>
            <h3 className="font-display uppercase font-bold text-xl sm:text-2xl md:text-3xl mt-2">{t("about.missionTitle")}</h3>
            <p className="mt-5 text-[#111111]/80 leading-relaxed">{pick(siteContent?.mission)}</p>
          </div>
          <div className="bg-[#111111] text-white p-10 block-shadow-orange" data-testid="vision-card">
            <Eye className="text-[#FF5A00]" size={32} />
            <span className="font-mono-rtc text-[11px] uppercase tracking-[0.3em] text-[#FF5A00] block mt-4">{t("about.visionLabel")}</span>
            <h3 className="font-display uppercase font-bold text-xl sm:text-2xl md:text-3xl mt-2">{t("about.visionTitle")}</h3>
            <p className="mt-5 text-white/80 leading-relaxed">{pick(siteContent?.vision)}</p>
          </div>
        </div>
      </section>

      {values.length > 0 && (
        <section className="py-10 md:py-14 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeader eyebrow={t("about.valuesEyebrow")} title={t("about.valuesTitle")} subtitle={t("about.valuesSubtitle")} />
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {values.map((v, i) => {
                const Icon = [ShieldCheck, HardHat, Award, Heart][i % 4];
                return (
                  <div key={i} className="bg-white border-2 border-[#111111] p-6 hover:border-[#FF5A00] hover:bg-[#FFF7F2] block-shadow-to-orange transition-all duration-300 group cursor-default" data-testid={`about-value-${i}`}>
                    <Icon size={26} className="text-[#FF5A00] group-hover:scale-110 transition-transform duration-300" />
                    <h4 className="font-display text-2xl md:text-3xl uppercase font-bold mt-4 text-[#111111] group-hover:text-[#FF5A00] transition-colors duration-300">{pick({ en: v.title_en, ar: v.title_ar })}</h4>
                    <p className="mt-2 text-sm text-[#52525B] leading-relaxed">{pick({ en: v.text_en, ar: v.text_ar })}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="py-10 md:py-14 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-8 md:gap-10 items-center">
          <div className="md:col-span-7 group">
            <div className="relative border-2 border-[#111111] block-shadow-to-orange overflow-hidden bg-white hover:border-[#FF5A00] transition-all duration-300">
              <img src="/team-rtc.jpg" alt="RTC team — skilled operators and crews" className="w-full h-auto block group-hover:scale-105 transition-transform duration-700" />
              {/* Caption badge */}
              <div className="absolute bottom-3 left-3 bg-[#FF5A00] text-white px-3 py-1.5 font-mono-rtc text-[10px] uppercase tracking-widest font-bold">
                The RTC Crew
              </div>
            </div>
          </div>
          <div className="md:col-span-5">
            <SectionHeader eyebrow={t("about.teamEyebrow")} title={t("about.teamTitle")} subtitle={t("about.teamSubtitle")} />
            <div className="mt-6 flex items-center gap-3">
              <Users size={20} className="text-[#FF5A00]" />
              <span className="font-mono-rtc text-xs uppercase tracking-widest text-[#52525B]">{t("about.teamCaption")}</span>
            </div>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 bg-[#111111] text-white px-7 py-3.5 font-bold uppercase tracking-wide hover:bg-[#FF5A00] transition-colors shadow-[3px_3px_0_#FF5A00] hover:shadow-[5px_5px_0_#111111]" data-testid="about-cta-contact">
              {t("common.workWithUs")} <ArrowRight size={16} className="rtl:rotate-180" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
